import React, { useEffect, useMemo, useState } from 'react';
import { AdminCrudShell } from '@/components/admin/AdminCrudShell';
import { Badge } from '@/components/ui/badge';
import { adminListClubsCatalog } from '@/lib/api';

const STATUS_OPTS = [
  { value: 'active', label: 'Activa' },
  { value: 'inactive', label: 'Inactiva' },
];

// Etiqueta amigable del `status` del club para mostrarla junto al nombre
// cuando el club no esté activo. Reutiliza el mismo vocabulario que el
// catálogo admin existente.
const CLUB_STATUS_LABEL = {
  active: 'Activo',
  inactive: 'Inactivo',
  hidden: 'Oculto',
  draft: 'Borrador',
  archived: 'Archivado',
};

function clubStatusText(status) {
  if (!status) return 'Desconocido';
  return CLUB_STATUS_LABEL[status] || status;
}

export default function AdminRevenueShare() {
  // Cache de clubes para resolver nombres en la tabla y poblar el selector.
  // Reutilizamos exclusivamente `adminListClubsCatalog`, que ya existe.
  // Devuelve todos los clubes no borrados; para los borrados caerá al
  // fallback "Club no encontrado" + UUID.
  const [clubs, setClubs] = useState([]);
  const [clubsLoading, setClubsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setClubsLoading(true);
      try {
        const data = await adminListClubsCatalog();
        if (!cancelled) setClubs((data && data.items) || []);
      } catch {
        if (!cancelled) setClubs([]);
      } finally {
        if (!cancelled) setClubsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const clubsById = useMemo(() => {
    const m = new Map();
    for (const c of clubs) m.set(c.id, c);
    return m;
  }, [clubs]);

  // Para "Crear/Editar regla": sólo clubes activos (por requerimiento).
  // En edición, si el `club_id` original pertenece a un club no activo,
  // añadimos esa opción puntual al final del listado para que el valor
  // actual sea visible y, si no se cambia, se conserve tal cual.
  const activeClubsOptions = useMemo(
    () => clubs
      .filter((c) => c.status === 'active')
      .map((c) => ({ value: c.id, label: c.name || c.slug || c.id })),
    [clubs],
  );

  // Función `options` que recibe los valores actuales del formulario para
  // incluir opcionalmente la opción "legacy" cuando se está editando una
  // regla cuyo club ya no está activo.
  const clubFieldOptions = (allValues = {}) => {
    const current = allValues.club_id || '';
    const base = activeClubsOptions;
    if (!current) return base;
    const alreadyListed = base.some((o) => o.value === current);
    if (alreadyListed) return base;
    const cur = clubsById.get(current);
    const label = cur
      ? `${cur.name || cur.slug || current} · ${clubStatusText(cur.status)}`
      : `Club no encontrado · ${current.slice(0, 8)}…`;
    return [...base, { value: current, label }];
  };

  return (
    <AdminCrudShell
      title="Revenue share por club"
      subtitle="Configura el porcentaje de ingresos que corresponde a cada club."
      entity="revenue-share"
      testIdPrefix="admin-revenue-share"
      addLabel="Nueva regla"
      initialForm={{ club_id: '', percentage: 0, active_from: '', active_to: '', status: 'active', notes: '' }}
      columns={[
        {
          key: 'club_id',
          label: 'Club',
          // Columna más ancha (~28%) y permitiendo hasta 2 líneas para
          // nombres largos. El UUID aparece truncado debajo en pequeño.
          className: 'w-[28%] min-w-[12rem]',
          render: (r) => {
            const id = r.club_id || '';
            const club = id ? clubsById.get(id) : null;
            const name = club
              ? (club.name || club.slug || id)
              : (clubsLoading ? '…' : 'Club no encontrado');
            const isActive = club ? club.status === 'active' : false;
            const showStatus = club && !isActive;
            return (
              <div
                className="min-w-0"
                data-testid={`admin-revenue-share-club-cell-${r.id}`}
              >
                <div className="flex items-start gap-1.5 flex-wrap">
                  <span
                    className={`text-sm font-medium text-[var(--ns-text)] line-clamp-2 ${club ? '' : 'italic text-[var(--ns-text-muted)]'}`}
                    data-testid={`admin-revenue-share-club-name-${r.id}`}
                  >
                    {name}
                  </span>
                  {showStatus ? (
                    <Badge className="h-4 px-1.5 text-[10px] uppercase bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)]">
                      {clubStatusText(club.status)}
                    </Badge>
                  ) : null}
                </div>
                <p
                  className="text-[10px] font-mono text-[var(--ns-text-subtle)] mt-0.5 truncate"
                  title={id}
                  data-testid={`admin-revenue-share-club-uuid-${r.id}`}
                >
                  {id ? `${id.slice(0, 8)}…` : '—'}
                </p>
              </div>
            );
          },
        },
        { key: 'percentage', label: 'Porcentaje', render: (r) => <span className="font-semibold text-[var(--ns-text)]">{Number(r.percentage || 0).toFixed(2)}%</span> },
        { key: 'active_from', label: 'Desde' },
        { key: 'active_to', label: 'Hasta' },
        { key: 'status', label: 'Estado', render: (r) => <Badge className={`text-[10px] uppercase ${r.status === 'active' ? 'bg-[var(--ns-gold)]/20 text-[var(--ns-gold-700)]' : 'bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)]'}`}>{r.status}</Badge> },
        { key: 'notes', label: 'Notas' },
      ]}
      formFields={[
        {
          name: 'club_id',
          label: 'Club',
          type: 'select',
          required: true,
          placeholder: clubsLoading ? 'Cargando clubes…' : 'Selecciona un club',
          options: clubFieldOptions,
          hint: 'Selecciona el club. El identificador interno (UUID) se conserva al guardar.',
        },
        { name: 'percentage', label: 'Porcentaje (0-100)', type: 'number', required: true, hint: 'P. ej. 70 = 70%', lowercase: false },
        { name: 'active_from', label: 'Activa desde (ISO)', placeholder: '2026-01-01T00:00:00Z', lowercase: false },
        { name: 'active_to', label: 'Activa hasta (ISO, opcional)', placeholder: '2027-01-01T00:00:00Z', lowercase: false },
        { name: 'status', label: 'Estado', type: 'select', options: STATUS_OPTS, required: true },
        { name: 'notes', label: 'Notas', type: 'textarea' },
      ]}
    />
  );
}
